/**
 * Service de gestion des contenus génériques
 */

import { API_CONFIG, getAuthHeaders, handleApiError, ApiResponse } from './apiConfig'

// Interfaces pour les données
export interface ContentFilters {
  type?: string
  category?: string
  tag?: string
  search?: string
  featured?: boolean
  sort_by?: 'published_at' | 'views_count' | 'likes_count' | 'title'
  sort_order?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

export interface Content {
  id: number
  type: string
  title: string
  slug: string
  description?: string
  content?: string
  image?: string
  images?: string[]
  video_url?: string
  metadata?: Record<string, any>
  category?: string
  tags?: string[]
  status: 'draft' | 'published' | 'archived'
  is_featured: boolean
  is_active: boolean
  author_id?: number
  author?: {
    id: number
    nom: string
    prenom: string
    email: string
  }
  views_count: number
  likes_count: number
  comments_count: number
  shares_count: number
  is_liked: boolean
  is_favorited: boolean
  published_at: string
  created_at: string
  updated_at: string
}

export interface Comment {
  id: number
  user_id: number
  content_id: number
  parent_id?: number
  comment: string
  is_approved: boolean
  likes_count: number
  user: {
    id: number
    nom: string
    prenom: string
    email: string
  }
  replies?: Comment[]
  created_at: string
  updated_at: string
}

export interface Category {
  category: string
  count: number
}

export interface Tag {
  tag: string
  count: number
}

class ContentService {
  /**
   * Récupérer une liste de contenus avec filtres
   */
  async getContents(filters: ContentFilters = {}): Promise<ApiResponse<{ data: Content[], total: number }>> {
    try {
      const params = new URLSearchParams()
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })

      const url = `${API_CONFIG.baseURL}/contents${params.toString() ? '?' + params.toString() : ''}`
      
      const response = await fetch(url, {
        method: 'GET',
        headers: API_CONFIG.headers
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer un contenu par son slug
   */
  async getContent(slug: string): Promise<ApiResponse<Content>> {
    try {
      const headers = getAuthHeaders()
      
      const response = await fetch(`${API_CONFIG.baseURL}/contents/${slug}`, {
        method: 'GET',
        headers: headers
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Liker/Unliker un contenu
   */
  async toggleLike(contentId: number): Promise<ApiResponse<{ liked: boolean, likes_count: number }>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/contents/${contentId}/like`, {
        method: 'POST',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Ajouter/Retirer des favoris
   */
  async toggleFavorite(contentId: number): Promise<ApiResponse<{ favorited: boolean }>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/contents/${contentId}/favorite`, {
        method: 'POST',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Partager un contenu
   */
  async shareContent(contentId: number, platform?: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/contents/${contentId}/share`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ platform })
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Ajouter un commentaire
   */
  async addComment(contentId: number, comment: string, parentId?: number): Promise<ApiResponse<Comment>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/contents/${contentId}/comments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ comment, parent_id: parentId })
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les commentaires d'un contenu
   */
  async getComments(contentId: number, page = 1, perPage = 10): Promise<ApiResponse<Comment[]>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/contents/${contentId}/comments?page=${page}&per_page=${perPage}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les favoris de l'utilisateur
   */
  async getFavorites(page = 1, perPage = 10): Promise<ApiResponse<Content[]>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/favorites?page=${page}&per_page=${perPage}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les catégories disponibles
   */
  async getCategories(type?: string): Promise<ApiResponse<Category[]>> {
    try {
      const url = type 
        ? `${API_CONFIG.baseURL}/contents/categories/list?type=${type}`
        : `${API_CONFIG.baseURL}/contents/categories/list`
      
      const response = await fetch(url, {
        method: 'GET',
        headers: API_CONFIG.headers
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les tags disponibles
   */
  async getTags(type?: string): Promise<ApiResponse<Tag[]>> {
    try {
      const url = type 
        ? `${API_CONFIG.baseURL}/contents/tags/list?type=${type}`
        : `${API_CONFIG.baseURL}/contents/tags/list`
      
      const response = await fetch(url, {
        method: 'GET',
        headers: API_CONFIG.headers
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Partager sur les réseaux sociaux
   */
  shareOnSocial(content: Content, platform: 'facebook' | 'twitter' | 'whatsapp' | 'linkedin') {
    const url = `${window.location.origin}/content/${content.slug}`
    const text = content.title
    
    let shareUrl = ''
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
        break
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
        break
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`
        break
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
        break
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400')
      // Enregistrer le partage dans l'API
      this.shareContent(content.id, platform)
    }
  }

  /**
   * Récupérer les FAQs
   */
  async getFaqs(): Promise<ApiResponse<any[]>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/faq`, {
        method: 'GET',
        headers: API_CONFIG.headers
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les tutoriels
   */
  async getTutoriels(): Promise<ApiResponse<any[]>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/tutoriels`, {
        method: 'GET',
        headers: API_CONFIG.headers
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer un tutoriel par son slug
   */
  async getTutorielBySlug(slug: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/tutoriels/${slug}`, {
        method: 'GET',
        headers: API_CONFIG.headers
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }
}

// Export d'une instance singleton
export default new ContentService()

